import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Card,
  CardContent,
  Button,
  Typography,
  Box,
  LinearProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress
} from '@mui/material';
import {
  Flip as FlipIcon,
  ArrowBack as ArrowBackIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Home as HomeIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { getDeck } from '../services/decks';
import { calculateNextReview, updateCardProgress } from '../services/study';
import { styled } from '@mui/material/styles';

// Container principal de la carte
const CardContainer = styled(Box)(({ theme, width, height }) => ({
  width: width || 500,
  height: height || 300,
  margin: '0 auto',
  marginBottom: theme.spacing(3)
}));

// La carte elle-même avec animation
const StyledCard = styled(Card)(({ slideDirection, isNewCard, glowSide }) => ({
  width: '100%',
  height: '100%',
  cursor: 'pointer',
  backgroundColor: 'rgba(18, 18, 18, 0.8)',
  transition: 'transform 0.5s ease-in-out, opacity 0.5s ease-in-out, box-shadow .5s ease-in-out',
  transform: slideDirection === 'right' 
    ? 'translateX(100%)'
    : slideDirection === 'left'
    ? 'translateX(-100%)'
    : isNewCard 
    ? 'scale(0.8)'
    : 'scale(1)',
  opacity: slideDirection ? 0 : 1,
  boxShadow: glowSide === 'left'
    ? '-12px 0 15px rgba(244, 67, 54, 0.03)'
    : glowSide === 'right'
    ? '12px 0 15px rgba(76, 175, 80, 0.03)'
    : 'none',
  position: 'relative',
  ...(isNewCard && {
    animation: 'scaleIn 0.5s ease-out forwards'
  }),
  '@keyframes scaleIn': {
    '0%': {
      transform: 'scale(0.8)',
      opacity: 0
    },
    '100%': {
      transform: 'scale(1)',
      opacity: 1
    }
  }
}));

// Conteneur du contenu simple
const StyledCardContent = styled(CardContent)(({ fontSize }) => ({
  height: '100%',
  padding: 8,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'column',
  gap: 8,
  position: 'relative',
  zIndex: 0,
  '& .text': {
    fontSize: `${fontSize}px`,
    textAlign: 'center',
    wordBreak: 'break-word',
    whiteSpace: 'pre-wrap'
  }
}));

// Zones cliquables pour la validation/refus
const ClickZone = styled(Box)({
  position: 'absolute',
  top: 0,
  height: '100%',
  width: '50%',
  cursor: 'pointer',
  zIndex: 1
});

function Study() {
  const { id: deckId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const forceReview = searchParams.get('forceReview') === 'true';
  
  const [deck, setDeck] = useState(null);
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [cardsToReview, setCardsToReview] = useState([]);
  const [stats, setStats] = useState({ correct: 0, incorrect: 0 });
  const [slideDirection, setSlideDirection] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isNewCard, setIsNewCard] = useState(false);
  const [glowSide, setGlowSide] = useState(null);
  const [showStats, setShowStats] = useState(false);
  const [isFinished, setIsFinished] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const [responsiveDimensions, setResponsiveDimensions] = useState({
    width: 500,
    height: 300,
    scale: 1
  });

  // Calculer les dimensions responsives
  useEffect(() => {
    const updateDimensions = () => {
      if (!deck) return;

      // Utiliser clientWidth pour une meilleure compatibilité mobile
      const viewportWidth = document.documentElement.clientWidth;
      
      // Calculer le padding en fonction de la taille de l'écran
      const containerPadding = viewportWidth < 600 ? 16 : 32; // 8px ou 16px de chaque côté
      const availableWidth = viewportWidth - containerPadding;
      
      const originalWidth = deck.cardWidth || 500;
      const originalHeight = deck.cardHeight || 300;

      // Calculer la largeur responsive
      const width = Math.min(availableWidth, originalWidth);
      // Calculer le ratio d'échelle
      const scale = width / originalWidth;
      // Calculer la hauteur proportionnelle
      const height = originalHeight * scale;

      setResponsiveDimensions({ width, height, scale });
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, [deck]);

  const prepareDeckBasedOnMode = (deck) => {
    if (!deck || !deck.cards) return deck;
    
    const preparedDeck = { ...deck };
    const preparedCards = [...deck.cards].map(card => ({ ...card }));

    // Assigner les tailles de police par défaut
    preparedCards.forEach(card => {
      card.frontFontSize = deck.frontFontSize;
      card.backFontSize = deck.backFontSize;
    });

    switch (deck.studyMode) {
      case 'back':
        // Inverser recto/verso pour toutes les cartes
        preparedCards.forEach(card => {
          const tempFront = card.front;
          const tempFrontColor = card.frontColor;
          const tempFrontImage = card.frontImageUrl;

          card.front = card.back;
          card.frontColor = card.backColor;
          card.frontImageUrl = card.backImageUrl;

          card.back = tempFront;
          card.backColor = tempFrontColor;
          card.backImageUrl = tempFrontImage;

          // Assigner les tailles de police
          card.frontFontSize = deck.backFontSize;
          card.backFontSize = deck.frontFontSize;
        });
        break;

      case 'random':
        // Inverser aléatoirement pour chaque carte
        preparedCards.forEach(card => {
          if (Math.random() < 0.5) {
            const tempFront = card.front;
            const tempFrontColor = card.frontColor;
            const tempFrontImage = card.frontImageUrl;

            card.front = card.back;
            card.frontColor = card.backColor;
            card.frontImageUrl = card.backImageUrl;

            card.back = tempFront;
            card.backColor = tempFrontColor;
            card.backImageUrl = tempFrontImage;

            // Assigner les tailles de police
            card.frontFontSize = deck.backFontSize;
            card.backFontSize = deck.frontFontSize;
          }
        });
        break;

      case 'front':
      default:
        // Garder l'ordre original
        break;
    }

    preparedDeck.cards = preparedCards;
    return preparedDeck;
  };

  useEffect(() => {
    loadDeck(forceReview);
  }, [deckId, forceReview]);

  const loadDeck = async (forceReview = false) => {
    try {
      setIsLoading(true);
      const deckData = await getDeck(deckId);
      const preparedData = prepareDeckBasedOnMode(deckData);
     //console.log('Deck préparé:', preparedData);
      setDeck(preparedData);

      const reviewCards = forceReview 
        ? preparedData.cards 
        : preparedData.cards.filter(card => !card.nextReview || new Date(card.nextReview).setHours(0, 0, 0, 0) <= new Date().setHours(0, 0, 0, 0));
      
      const shuffledCards = [...reviewCards].sort(() => Math.random() - 0.5);
      setCardsToReview(shuffledCards);
    } catch (error) {
      console.error('Erreur lors du chargement du paquet:', error);
    } finally {
      setIsLoading(false);
      setIsInitialized(true);
    }
  };

  const handleFlip = () => {
    setIsFlipped(!isFlipped);
  };

  const handleResponse = async (quality) => {
    if (isAnimating) return;

    if (!hasStarted) {
      setHasStarted(true);
    }

    setIsAnimating(true);
    setGlowSide(null);
    setSlideDirection(quality >= 3 ? 'right' : 'left');

    const currentCard = cardsToReview[currentCardIndex];
    const nextReviewData = calculateNextReview(currentCard, quality);

    try {
      // Ne mettre à jour les statistiques que si nous ne sommes pas en mode révision
      if (!forceReview) {
        await updateCardProgress(deckId, currentCard.id, {
          ...nextReviewData,
          lastReviewed: new Date(),
          reviewCount: (currentCard.reviewCount || 0) + 1,
          successCount: quality >= 3 ? (currentCard.successCount || 0) + 1 : currentCard.successCount || 0
        });
      }

      // Mettre à jour les statistiques de la session uniquement
      setStats(prev => ({
        correct: prev.correct + (quality >= 3 ? 1 : 0),
        incorrect: prev.incorrect + (quality < 3 ? 1 : 0)
      }));

      const isLastCard = currentCardIndex === cardsToReview.length - 1;

      setTimeout(() => {
        if (!isLastCard) {
          setCurrentCardIndex(prev => prev + 1);
          setIsFlipped(false);
          setSlideDirection(null);
          setIsNewCard(true);
          
          setTimeout(() => {
            setIsNewCard(false);
            setIsAnimating(false);
          }, 500);
        } else {
          setCurrentCardIndex(cardsToReview.length);
          setIsFinished(true);
          setShowStats(true);
        }
      }, 500);

    } catch (error) {
      console.error('Erreur lors de la mise à jour:', error);
      setIsAnimating(false);
      setSlideDirection(null);
    }
  };

  const handleRestart = () => {
    setShowStats(false);
    setCurrentCardIndex(0);
    setIsFlipped(false);
    setSlideDirection(null);
    setIsAnimating(false);
    setStats({ correct: 0, incorrect: 0 });
    setIsFinished(false);
    setIsNewCard(true);
    setGlowSide(null);
    
    // Mélanger à nouveau les cartes pour la nouvelle session
    setCardsToReview(cards => [...cards].sort(() => Math.random() - 0.5));
    
    setTimeout(() => {
      setIsNewCard(false);
    }, 500);
  };

  const currentCard = cardsToReview[currentCardIndex];
  const progress = Math.min((currentCardIndex / cardsToReview.length) * 100, 100);

  const renderCardContent = (card) => {
    const content = isFlipped ? card.back : card.front;
    const color = isFlipped ? card.backColor : card.frontColor;
    const imageUrl = isFlipped ? card.backImageUrl : card.frontImageUrl;

    const commonBoxStyles = {
      height: '100%',
      width: '100%',
      position: 'absolute',
      top: 0,
      left: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    };

    // Cas 1: Image seule
    if (imageUrl && !content) {
      return (
        <Box sx={commonBoxStyles}>
          <Box
            component="img"
            src={`${process.env.REACT_APP_API_URL.replace(/\/api$/, '')}${imageUrl}`}
            alt=""
            sx={{
              width: '100%',
              maxHeight: `${80 * responsiveDimensions.scale}%`,
              objectFit: 'contain'
            }}
          />
        </Box>
      );
    }

    // Cas 2: Texte seul
    if (!imageUrl && content) {
      return (
        <Box sx={commonBoxStyles}>
          <Typography 
            variant="h5"
            sx={{ 
              color: color,
              wordBreak: 'break-word',
              whiteSpace: 'pre-wrap',
              textAlign: 'center',
              px: 2
            }}
          >
            {content}
          </Typography>
        </Box>
      );
    }

    // Cas 3: Image et texte
    return (
      <Box sx={commonBoxStyles}>
        <Box sx={{ 
          height: '100%',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          p: 2
        }}>
          <Box sx={{ 
            height: '70%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Box
              component="img"
              src={`${process.env.REACT_APP_API_URL.replace(/\/api$/, '')}${imageUrl}`}
              alt=""
              sx={{
                width: '100%',
                maxHeight: `${70 * responsiveDimensions.scale}%`,
                objectFit: 'contain'
              }}
            />
          </Box>
          <Box sx={{
            height: '25%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Typography 
              variant="h5"
              sx={{ 
                color: color,
                wordBreak: 'break-word',
                whiteSpace: 'pre-wrap',
                textAlign: 'center'
              }}
            >
              {content}
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  };

  const handleCardClick = (event, cardRect) => {
    if (!isFlipped || isAnimating) {
      handleFlip();
      return;
    }

    // Calculer si le clic est sur la partie gauche ou droite
    const clickX = event.clientX - cardRect.left;
    const isRightSide = clickX > cardRect.width / 2;

    handleResponse(isRightSide ? 5 : 1);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isInitialized) {
    return null;
  }

  if (!forceReview && (!cardsToReview || cardsToReview.length === 0)) {
    return (
      <Container maxWidth="sm" sx={{ mt: 4 }}>
        <Typography variant="h5" align="center">
          Aucune carte à étudier pour le moment !
        </Typography>
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 2 }}>
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/dashboard')}
          >
            Retour au tableau de bord
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => loadDeck(true)}
          >
            Réviser quand même
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container 
      maxWidth="lg" 
      sx={{ 
        py: 4,
        px: { xs: 1, sm: 2 } // Réduire le padding horizontal sur les petits écrans
      }}
    >
      <Box sx={{ mb: 2 }}>
        <LinearProgress variant="determinate" value={progress} />
        <Typography variant="body2" align="right" sx={{ mt: 1 }}>
          {currentCardIndex} / {cardsToReview.length}
        </Typography>
      </Box>

      <CardContainer width={responsiveDimensions.width} height={responsiveDimensions.height}>
        <StyledCard
          onClick={(e) => {
            const cardRect = e.currentTarget.getBoundingClientRect();
            handleCardClick(e, cardRect);
          }}
          slideDirection={slideDirection}
          isNewCard={isNewCard}
          glowSide={glowSide}
        >
          <StyledCardContent 
            fontSize={
              (isFlipped ? currentCard?.backFontSize : currentCard?.frontFontSize) * responsiveDimensions.scale
            }
          >
            {currentCard?.frontImageUrl && !isFlipped && (
              <Box
                component="img"
                src={`${process.env.REACT_APP_API_URL.replace(/\/api$/, '')}${currentCard.frontImageUrl}`}
                alt=""
                sx={{
                  width: '100%',
                  maxHeight: '80%',
                  objectFit: 'contain'
                }}
              />
            )}
            {currentCard?.backImageUrl && isFlipped && (
              <Box
                component="img"
                src={`${process.env.REACT_APP_API_URL.replace(/\/api$/, '')}${currentCard.backImageUrl}`}
                alt=""
                sx={{
                  width: '100%',
                  maxHeight: '80%',
                  objectFit: 'contain'
                }}
              />
            )}
            <Typography 
              className="text"
              sx={{ 
                color: isFlipped ? currentCard?.backColor : currentCard?.frontColor 
              }}
            >
              {isFlipped ? currentCard?.back : currentCard?.front}
            </Typography>

            {isFlipped && !isAnimating && (
              <>
                <ClickZone
                  sx={{ left: 0 }}
                  onMouseEnter={() => setGlowSide('left')}
                  onMouseLeave={() => setGlowSide(null)}
                />
                <ClickZone
                  sx={{ right: 0 }}
                  onMouseEnter={() => setGlowSide('right')}
                  onMouseLeave={() => setGlowSide(null)}
                />
              </>
            )}
          </StyledCardContent>
        </StyledCard>
      </CardContainer>

      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        gap: 2,
        transition: 'opacity 0.3s ease-in-out',
        opacity: isFinished ? 0 : 1
      }}>
        <IconButton
          color="error"
          size="large"
          onClick={() => handleResponse(1)}
          disabled={!isFlipped || isAnimating || isFinished}
        >
          <CloseIcon />
        </IconButton>
        <IconButton
          color="primary"
          size="large"
          onClick={handleFlip}
          disabled={isAnimating || isFinished}
        >
          <FlipIcon />
        </IconButton>
        <IconButton
          color="success"
          size="large"
          onClick={() => handleResponse(5)}
          disabled={!isFlipped || isAnimating || isFinished}
        >
          <CheckIcon />
        </IconButton>
      </Box>

      <Dialog 
        open={showStats} 
        onClose={() => {}}
        disableEscapeKeyDown
        disableScrollLock
        keepMounted
        sx={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.8)'
          }
        }}
        PaperProps={{
          elevation: 24,
          sx: {
            width: Math.min(responsiveDimensions.width, 400),
            maxHeight: '90vh',
            overflowY: 'auto',
            position: 'absolute',
            top: '0',
            left: '0',
            transform: 'translate(calc(50vw - 50%), calc(50vh - 50%))',
            m: 0
          }
        }}
      >
        <DialogTitle sx={{ 
          textAlign: 'center', 
          pb: 2,
          fontSize: Math.max(16, Math.min(24, 20 * responsiveDimensions.scale))
        }}>
          {forceReview ? "Session de révision terminée !" : "Session d'apprentissage terminée !"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ 
            px: { xs: 1, sm: 2 },
            '& .MuiTypography-body1': {
              fontSize: Math.max(14, Math.min(16, 15 * responsiveDimensions.scale))
            }
          }}>
            {forceReview && (
              <Typography 
                variant="body2" 
                color="text.secondary" 
                sx={{ mb: 2, fontStyle: 'italic' }}
              >
                Mode révision : cette session n'a pas impacté les intervalles d'apprentissage
              </Typography>
            )}
            <Typography variant="body1" gutterBottom>
              Cartes révisées : {cardsToReview.length}
            </Typography>
            <Typography variant="body1" gutterBottom color="success.main">
              Réponses correctes : {stats.correct}
            </Typography>
            <Typography variant="body1" gutterBottom color="error.main">
              Réponses incorrectes : {stats.incorrect}
            </Typography>
            <Box sx={{ mt: 2, mb: 0.5 }}>
              <Typography variant="body1">
                Taux de réussite : {Math.round((stats.correct / cardsToReview.length) * 100)}%
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ 
          justifyContent: 'center', 
          gap: Math.max(4, Math.min(16, 8 * responsiveDimensions.scale)), // Espacement responsive
          pt: 0, 
          pb: 2,
          px: { xs: 1, sm: 2 }
        }}>
          <IconButton
            color="primary"
            size={responsiveDimensions.scale < 0.8 ? "medium" : "large"}
            onClick={() => navigate('/dashboard')}
          >
            <HomeIcon />
          </IconButton>
          <IconButton
            color="primary"
            size={responsiveDimensions.scale < 0.8 ? "medium" : "large"}
            onClick={handleRestart}
          >
            <RefreshIcon />
          </IconButton>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default Study; 